/**
 * Historial de asistencia del trabajador (006 · T020; contracts/staff-api.md §1, research R-7,
 * R-16 y R-30).
 *
 * `GET /attendance/marks` es **autenticada** (cookie `fermin_staff_session` + `X-Fermin-Device`
 * firmada: la firma cubre la ruta con su query, igual que `request.url` en la API). La respuesta se
 * valida con `StaffAttendanceHistoryResponseSchema` de `@fermin/contracts`; aquí no se redefine
 * nada. Paginación por **días operativos completos** con `next_cursor` opaco.
 *
 * - `useHistory()`: pestaña Asistencia, infinita por `next_cursor`, 14 días por página.
 * - `useRecentDays()`: "Últimas marcaciones" de la home, 3 días.
 * - Ambas con `refetchOnWindowFocus: true` (volver a la PWA refresca; el `QueryClient` global
 *   sigue en `false`) y el `staleTime` global de 10 s. Nada se persiste (wiring 006 §0.7).
 * - `invalidateAttendanceViews(queryClient)`: tras marcar, invalida el estado y **todas** las
 *   variantes del historial (R-16).
 */
import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  STAFF_HISTORY_PAGE_DAYS_DEFAULT,
  StaffAttendanceHistoryResponseSchema,
  type StaffAttendanceHistoryResponse,
} from '@fermin/contracts';

import { api, type ApiError } from '../../api/client';
import { ATTENDANCE_STATE_QUERY_KEY } from '../attendance/api';
import { STAFF_REGULARIZATIONS_QUERY_KEY } from '../regularization/keys';
import { STAFF_DAYS_QUERY_KEY } from '../schedule/keys';

/** Prefijo común de todas las consultas del historial (home y pestaña). */
export const STAFF_HISTORY_QUERY_KEY = ['staff-history'] as const;

/** Días por página en la pestaña Asistencia (= defecto del contrato, 14). */
export const HISTORY_PAGE_DAYS = STAFF_HISTORY_PAGE_DAYS_DEFAULT;

/** Días de "Últimas marcaciones" en la home (R-30). */
export const RECENT_DAYS = 3;

export const STAFF_HISTORY_INFINITE_QUERY_KEY = [
  ...STAFF_HISTORY_QUERY_KEY,
  'pages',
  { limit: HISTORY_PAGE_DAYS },
] as const;

export const STAFF_RECENT_DAYS_QUERY_KEY = [
  ...STAFF_HISTORY_QUERY_KEY,
  'recent',
  { limit: RECENT_DAYS },
] as const;

export interface FetchHistoryPageInput {
  cursor?: string | null;
  limit?: number;
  signal?: AbortSignal;
}

/**
 * `GET /attendance/marks?limit=…&cursor=…`. El cliente no acepta la query como objeto: la ruta se
 * arma aquí con `URLSearchParams` (wiring 006 §6.1).
 */
export function fetchHistoryPage({
  cursor,
  limit = HISTORY_PAGE_DAYS,
  signal,
}: FetchHistoryPageInput = {}): Promise<StaffAttendanceHistoryResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) {
    params.set('cursor', cursor);
  }
  return api.get(`/attendance/marks?${params.toString()}`, {
    schema: StaffAttendanceHistoryResponseSchema,
    ...(signal ? { signal } : {}),
  });
}

export interface HistoryQueryOptions {
  enabled?: boolean;
}

/** Pestaña Asistencia: páginas de 14 días hasta que `next_cursor` sea `null` (ventana de 60). */
export function useHistory(
  options: HistoryQueryOptions = {},
): UseInfiniteQueryResult<InfiniteData<StaffAttendanceHistoryResponse, string | null>, ApiError> {
  return useInfiniteQuery<
    StaffAttendanceHistoryResponse,
    ApiError,
    InfiniteData<StaffAttendanceHistoryResponse, string | null>,
    typeof STAFF_HISTORY_INFINITE_QUERY_KEY,
    string | null
  >({
    queryKey: STAFF_HISTORY_INFINITE_QUERY_KEY,
    queryFn: ({ pageParam, signal }) =>
      fetchHistoryPage({ cursor: pageParam, limit: HISTORY_PAGE_DAYS, signal }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: true,
  });
}

/** Home: los 3 días operativos más recientes con marcas. */
export function useRecentDays(
  options: HistoryQueryOptions = {},
): UseQueryResult<StaffAttendanceHistoryResponse, ApiError> {
  return useQuery<StaffAttendanceHistoryResponse, ApiError>({
    queryKey: STAFF_RECENT_DAYS_QUERY_KEY,
    queryFn: ({ signal }) => fetchHistoryPage({ limit: RECENT_DAYS, signal }),
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: true,
  });
}

/**
 * Tras marcar (o al volver de `/marcar`): estado IN/OUT + todas las variantes del historial y,
 * desde la 007 (T032, research R-16), las regularizaciones: enviar o cancelar una propuesta
 * también pasa por aquí. 008 · T095: y los días con su horario y estado (`STAFF_DAYS_QUERY_KEY`).
 */
export async function invalidateAttendanceViews(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ATTENDANCE_STATE_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: STAFF_HISTORY_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: STAFF_REGULARIZATIONS_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: STAFF_DAYS_QUERY_KEY }),
  ]);
}
